import React, { useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';

import ApplicationLoading from '../../../../common/ApplicationLoading';
import { Button } from '../../../../components/core';
import { agentHubPaths } from '../../../../util/constants';
import {
  useGetCredentialsQuery,
  usePatchOnboardingStepMutation,
  usePatchStoreReadinessMutation,
} from '../../../../store/modules/merchant/api';
import type { ShopifyOnboardingStep } from '../../../../store/modules/merchant/types';
import { toast } from '../../../../util/toast';
import { StatusChip } from '../../../ShopifyProvisioning/StatusChip';
import {
  areSkipifyStepsComplete,
  buttonPaddingSx,
  getApiErrorMessage,
  getInstallUrl,
  getMerchantOwnedSteps,
  getMerchantProvisioningStatus,
  getStep,
  getStepLabel,
  MERCHANT_OWNED_STEP_KEYS,
} from '../../../ShopifyProvisioning/utils';
import {
  ActionUrl,
  StatusRefreshLink,
  StepCard,
  WaitingHint,
} from '../components/ProvisioningStepParts';
import { useShopifyProvisioningEligibility } from './useShopifyProvisioningEligibility';

const InstallLinkPlaceholder = ({
  onCheckStatus,
  isRefreshing,
}: {
  onCheckStatus: () => void;
  isRefreshing: boolean;
}): JSX.Element => (
  <WaitingHint
    testId="ella-provisioning-install-url-placeholder"
    title="Your install link isn't ready yet"
  >
    Skipify is still setting up the app for your store.{' '}
    <StatusRefreshLink
      label="See if it's ready"
      onCheckStatus={onCheckStatus}
      isRefreshing={isRefreshing}
    />
  </WaitingHint>
);

const InstallLinkValue = ({ url }: { url: string }): JSX.Element => (
  <Stack spacing={1.25} data-testid="ella-provisioning-install-url">
    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
      Open this link in Shopify to install Skipify. After you install the app, click{' '}
      <Box component="span" sx={{ fontWeight: 700 }}>
        Check Status
      </Box>{' '}
      button above to confirm it connected.
    </Typography>
    <ActionUrl url={url} copyTitle="install link" />
  </Stack>
);

const ThemeEditorPlaceholder = ({
  oauthComplete,
  onCheckStatus,
  isRefreshing,
}: {
  oauthComplete: boolean;
  onCheckStatus: () => void;
  isRefreshing: boolean;
}): JSX.Element => (
  <WaitingHint
    testId="ella-provisioning-theme-editor-url-placeholder"
    title="Your theme editor link isn't ready yet"
  >
    {oauthComplete ? (
      <>
        We&apos;re still fetching the link to your Shopify theme editor.{' '}
        <StatusRefreshLink
          label="See if it's ready"
          onCheckStatus={onCheckStatus}
          isRefreshing={isRefreshing}
        />
      </>
    ) : (
      'Finish installing Skipify first. This link will appear after that step is connected.'
    )}
  </WaitingHint>
);

const ThemeEditorLinkValue = ({ url }: { url: string }): JSX.Element => (
  <Stack spacing={1.25} data-testid="ella-provisioning-theme-editor-url">
    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
      Open this link in Shopify to turn on the Skipify embed in your theme. Then tap Mark done.
    </Typography>
    <ActionUrl url={url} copyTitle="theme editor link" />
  </Stack>
);

const credentialConfiguration = (credential?: { data?: Record<string, unknown> }) => {
  const data = credential?.data as { configuration?: Record<string, unknown> } | undefined;
  return data?.configuration;
};

export const ProvisioningShopifyPage = (): JSX.Element => {
  const { merchantId, onboarding, isEligible, isLoading, isOnboardingError, refetchOnboarding } =
    useShopifyProvisioningEligibility();
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const {
    data: credentials,
    isLoading: areCredentialsLoading,
    refetch: refetchCredentials,
  } = useGetCredentialsQuery(
    { merchantId: merchantId ?? '', type: ['ecommerce'] },
    { skip: !merchantId || !onboarding },
  );
  const [patchStep, { isLoading: isPatchingStep }] = usePatchOnboardingStepMutation();
  const [patchStoreReadiness] = usePatchStoreReadinessMutation();

  if (isLoading) {
    return <ApplicationLoading />;
  }

  if (!isEligible) {
    return <Navigate to={agentHubPaths.DASHBOARD} replace />;
  }

  if (isOnboardingError || !onboarding) {
    return (
      <Box data-testid="ella-provisioning-shopify-page">
        <Alert severity="info">
          Shopify provisioning has not started for this store yet. A Skipify team member will set up
          your checklist once store onboarding begins.
        </Alert>
      </Box>
    );
  }

  const existingCredential = credentials?.ecommerce?.credentials.find(
    (credential) => credential.systemOfOrigin === 'shopify',
  );
  const config = credentialConfiguration(existingCredential);
  const clientId = typeof config?.clientId === 'string' ? config.clientId : undefined;
  const shopDomain = onboarding.externalPlatformId ?? '';
  const installUrl = getInstallUrl(onboarding);
  const appDeployDone = getStep(onboarding, 'app_deploy')?.status === 'done';
  const visibleInstallUrl = appDeployDone ? installUrl : undefined;
  const oauthComplete = getStep(onboarding, 'oauth_complete')?.status === 'done';
  const skipifyReady = areSkipifyStepsComplete(onboarding);
  const provisioningStatus = getMerchantProvisioningStatus(onboarding);
  const embedUrl =
    oauthComplete && clientId
      ? `https://${shopDomain}/admin/themes/current/editor?context=apps&activateAppId=${clientId}/sdk-embed`
      : undefined;

  const merchantSteps = getMerchantOwnedSteps(onboarding);
  const stepNumber = (key: ShopifyOnboardingStep['key']) =>
    MERCHANT_OWNED_STEP_KEYS.indexOf(key) + 1;
  const isStatusBusy = isRefreshingStatus || areCredentialsLoading;

  const checkStatus = async () => {
    setIsRefreshingStatus(true);
    try {
      const [onboardingResponse, credentialsResponse] = await Promise.all([
        refetchOnboarding(),
        refetchCredentials(),
      ]);
      const latestOnboarding =
        onboardingResponse.data?.systemOfOrigin === 'shopify'
          ? onboardingResponse.data
          : onboarding;
      const currentCredential = credentialsResponse.data?.ecommerce?.credentials.find(
        (credential) => credential.systemOfOrigin === 'shopify',
      );
      const currentConfig = credentialConfiguration(currentCredential);
      const oauthTokenPresent =
        typeof currentConfig?.refreshToken === 'string' && !!currentConfig.refreshToken;

      if (oauthTokenPresent && getStep(latestOnboarding, 'oauth_complete')?.status !== 'done') {
        await patchStep({
          merchantId: merchantId!,
          systemOfOrigin: 'shopify',
          key: 'oauth_complete',
          body: { status: 'done' },
        }).unwrap();
      }

      toast.success('Onboarding status refreshed.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to refresh onboarding status.'));
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const markEmbedDone = async () => {
    const previousEmbedStatus = getStep(onboarding, 'embed_enabled')?.status ?? 'pending';
    try {
      await patchStep({
        merchantId: merchantId!,
        systemOfOrigin: 'shopify',
        key: 'embed_enabled',
        body: { status: 'done' },
      }).unwrap();
      try {
        await patchStoreReadiness({
          merchantId: merchantId!,
          body: { storeReadiness: { status: 'complete' } },
        }).unwrap();
      } catch (readinessError) {
        try {
          await patchStep({
            merchantId: merchantId!,
            systemOfOrigin: 'shopify',
            key: 'embed_enabled',
            body: { status: previousEmbedStatus },
          }).unwrap();
        } catch {
          // Keep the original readiness error if the compensating revert also fails.
        }
        throw readinessError;
      }
      toast.success(`${getStepLabel('embed_enabled')} marked done.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to update this onboarding step.'));
    }
  };

  return (
    <Box data-testid="ella-provisioning-shopify-page">
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <StatusChip label={provisioningStatus.label} color={provisioningStatus.color} />
          </Stack>
          <Button
            text="Check Status"
            sx={buttonPaddingSx}
            onClick={() => {
              void checkStatus();
            }}
            disabled={isStatusBusy}
            loading={isRefreshingStatus}
          />
        </Stack>

        {!skipifyReady && (
          <Alert severity="info">
            Skipify is still preparing your Shopify store. The steps below will become actionable
            once that work is complete.
          </Alert>
        )}

        <Stack spacing={3}>
          {merchantSteps.map((step) => {
            if (step.key === 'oauth_complete') {
              return (
                <StepCard
                  key={step.key}
                  number={stepNumber(step.key)}
                  title="Install Skipify in Shopify"
                  status={step.status}
                >
                  {visibleInstallUrl ? (
                    <InstallLinkValue url={visibleInstallUrl} />
                  ) : (
                    <InstallLinkPlaceholder
                      onCheckStatus={() => {
                        void checkStatus();
                      }}
                      isRefreshing={isStatusBusy}
                    />
                  )}
                </StepCard>
              );
            }

            if (step.key === 'embed_enabled') {
              return (
                <StepCard
                  key={step.key}
                  number={stepNumber(step.key)}
                  title={getStepLabel(step.key)}
                  status={step.status}
                >
                  {embedUrl ? (
                    <ThemeEditorLinkValue url={embedUrl} />
                  ) : (
                    <ThemeEditorPlaceholder
                      oauthComplete={oauthComplete}
                      onCheckStatus={() => {
                        void checkStatus();
                      }}
                      isRefreshing={isStatusBusy}
                    />
                  )}
                  <Button
                    text="Mark done"
                    theme="secondary"
                    variant="outlined"
                    sx={buttonPaddingSx}
                    onClick={() => {
                      void markEmbedDone();
                    }}
                    disabled={
                      !oauthComplete || step.status === 'done' || isPatchingStep || !skipifyReady
                    }
                    loading={isPatchingStep}
                  />
                </StepCard>
              );
            }

            return null;
          })}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Need help? Contact your Skipify representative if any step is blocked.
        </Typography>
      </Stack>
    </Box>
  );
};
